import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import creditService from '../../services/creditService';

/**
 * Async Thunks for Credit API Calls
 */

// Fetch balance
export const fetchBalance = createAsyncThunk(
  'credits/fetchBalance',
  async ({ userId = null, companyId = null } = {}, { rejectWithValue }) => {
    try {
      const result = await creditService.getBalance(userId, companyId);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Check balance (document wizard pre-check)
export const checkBalance = createAsyncThunk(
  'credits/checkBalance',
  async (requiredCredits, { rejectWithValue }) => {
    try {
      const result = await creditService.checkBalance(requiredCredits);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Fetch transaction history
export const fetchTransactionHistory = createAsyncThunk(
  'credits/fetchTransactionHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const result = await creditService.getTransactionHistory(params);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Purchase credits
export const purchaseCredits = createAsyncThunk(
  'credits/purchaseCredits',
  async ({ packageId, paymentData, source = 'page' }, { rejectWithValue }) => {
    try {
      const result = await creditService.purchaseCredits(packageId, paymentData, source);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Add manual credits (admin)
export const addManualCredits = createAsyncThunk(
  'credits/addManualCredits',
  async (data, { rejectWithValue }) => {
    try {
      const result = await creditService.addManualCredits(data);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Subtract manual credits (admin)
export const subtractManualCredits = createAsyncThunk(
  'credits/subtractManualCredits',
  async (data, { rejectWithValue }) => {
    try {
      const result = await creditService.subtractManualCredits(data);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Get reports
export const getReports = createAsyncThunk(
  'credits/getReports',
  async (params = {}, { rejectWithValue }) => {
    try {
      const result = await creditService.getReports(params);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Get billing history (purchase transactions only)
export const getBillingHistory = createAsyncThunk(
  'credits/getBillingHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const result = await creditService.getBillingHistory(params);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Credit Slice
 */
const creditSlice = createSlice({
  name: 'credits',
  initialState: {
    balance: 0,
    balanceCheck: null, // {hasEnough, currentBalance, required, deficit}
    transactions: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0
    },
    reports: {
      totalSales: 0,
      totalManualAdded: 0,
      totalCreditsLoaded: 0,
      totalUsed: 0,
      documents: [],
      users: []
    },
    billingHistory: {
      transactions: [],
      total: 0,
      summary: {
        totalSpent: 0,
        totalPurchasedCredits: 0,
        totalCreditsLoaded: 0,
        currentBalance: 0
      }
    },
    billingLoading: false,
    billingError: null,
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearBalanceCheck: (state) => {
      state.balanceCheck = null;
    },
    clearReports: (state) => {
      state.reports = {
        totalSales: 0,
        totalManualAdded: 0,
        totalCreditsLoaded: 0,
        totalUsed: 0,
        documents: [],
        users: []
      };
    }
  },
  extraReducers: (builder) => {
    // Fetch Balance
    builder
      .addCase(fetchBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload.balance || 0;
      })
      .addCase(fetchBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch balance';
      });

    // Check Balance
    builder
      .addCase(checkBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balanceCheck = action.payload;
      })
      .addCase(checkBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to check balance';
      });

    // Fetch Transaction History
    builder
      .addCase(fetchTransactionHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions || [];
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 20,
          total: action.payload.total || 0
        };
      })
      .addCase(fetchTransactionHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch transaction history';
      });

    // Purchase Credits
    builder
      .addCase(purchaseCredits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(purchaseCredits.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload.balance || 0;
        // Add transaction to list (if available)
        if (action.payload.transaction) {
          state.transactions.unshift(action.payload.transaction);
          state.pagination.total += 1;
        }
      })
      .addCase(purchaseCredits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to purchase credits';
      });

    // Add Manual Credits
    builder
      .addCase(addManualCredits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addManualCredits.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload.balance || 0;
        // Add transaction to list (if available)
        if (action.payload.transaction) {
          state.transactions.unshift(action.payload.transaction);
          state.pagination.total += 1;
        }
      })
      .addCase(addManualCredits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to add manual credits';
      });

    // Subtract Manual Credits
    builder
      .addCase(subtractManualCredits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(subtractManualCredits.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload.balance || 0;
        // Add transaction to list (if available)
        if (action.payload.transaction) {
          state.transactions.unshift(action.payload.transaction);
          state.pagination.total += 1;
        }
      })
      .addCase(subtractManualCredits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to subtract manual credits';
      });

    // Get Reports
    builder
      .addCase(getReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = {
          totalSales: action.payload.totalSales || 0,
          totalManualAdded: action.payload.totalManualAdded || 0,
          totalCreditsLoaded: action.payload.totalCreditsLoaded || 0,
          totalUsed: action.payload.totalUsed || 0,
          documents: action.payload.documents || [],
          users: action.payload.users || []
        };
      })
      .addCase(getReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch reports';
      });

    // Get Billing History
    builder
      .addCase(getBillingHistory.pending, (state) => {
        state.billingLoading = true;
        state.billingError = null;
      })
      .addCase(getBillingHistory.fulfilled, (state, action) => {
        state.billingLoading = false;
        state.billingHistory = {
          transactions: action.payload.transactions || [],
          total: action.payload.total || 0,
          summary: action.payload.summary || {
            totalSpent: 0,
            totalPurchasedCredits: 0,
            totalCreditsLoaded: 0,
            currentBalance: 0
          }
        };
      })
      .addCase(getBillingHistory.rejected, (state, action) => {
        state.billingLoading = false;
        state.billingError = action.payload?.message || 'Failed to fetch billing history';
      });
  }
});

export const {
  clearError,
  clearBalanceCheck,
  clearReports
} = creditSlice.actions;

export default creditSlice.reducer;

